import React, { useContext, useEffect } from 'react';

import { TrackEventContext } from 'common/containers/EventContainer';
import AkiflowLandingHelmet from 'common/helmets/AkiflowLandingHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import UppercaseHeader from 'common/UppercaseHeader';
import LandingCaseStudyHeadingText from 'common/www/LandingCaseStudyHeadingText';

import AkiflowWordmark from 'img/landing/akiflow-wordmark.svg';

import 'css/components/www/_LandingCustomersPage.scss';

const AkiflowLanding = () => {
  const trackEvent = useContext(TrackEventContext);

  useEffect(() => {
    trackEvent('Viewed Akiflow Case Study');
  }, [trackEvent]);

  return (
    <div className="landingAkiflow landingCustomersPage">
      <AkiflowLandingHelmet />
      <div className="fullWidthContainer">
        <div className="header">
          <LandingCaseStudyHeadingText title="How Akiflow organized their feedback and started winning back churned customers" />
          <div className="headerVideo">
            <iframe
              width="560"
              height="315"
              src="https://www.youtube.com/embed/OfG8uAwxHRA?si=36zGxUd16KAjMttV"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen></iframe>
          </div>
        </div>
      </div>
      <div className="mainContent">
        <div className="mainContentText">
          <div className="left">
            <p>
              <a href="https://akiflow.com/" target="_blank" rel="noreferrer">
                Akiflow
              </a>{' '}
              is a productivity tool that helps you stay organized. Naturally, feedback organization
              is top of mind for Stefania Martinello, Akiflow’s product&nbsp;manager.
            </p>
            <p>
              Stefania works closely with developers and designers to bring new features to life.
              She also collaborates with marketing to communicate Akiflow’s value. With 15 teammates
              and thousands of customers, it’s a lot of work. And a lot of&nbsp;feedback.
            </p>
            <p className="quote">
              &ldquo;My primary goal is to ensure that Akiflow continues to provide value to its
              users. To make product decisions, I need to be sure about users’
              pain&nbsp;points.&rdquo;
            </p>
            <p>
              But how can you really know what your users are thinking? One of the best ways is to
              ask them&nbsp;directly.
            </p>
            <h2 className="sectionTitle">Problem</h2>
            <p>
              Before Canny, Stefania was onboarding users one by one. To track their feedback, she
              used&nbsp;Notion.
            </p>
            <p className="quote">
              &ldquo;As you can imagine, this quickly became a mess. I really needed to keep track
              of all the feedback. Plus, feedback was coming from various sources – email, Intercom,
              one-on-one calls, and so&nbsp;on.&rdquo;
            </p>
            <p>
              Because Akiflow is a small startup, Stefania’s role involves customer success and
              support too. So, she talks to customers every day and receives feedback and ideas
              from&nbsp;them.
            </p>
            <p className="quote">
              &ldquo;I’m obsessed with feedback. Someone taking the time to tell us how we can
              improve – that’s&nbsp;invaluable.&rdquo;
            </p>
            <p>
              Clearly, Stefania and her team at Akiflow value feedback very much. So tracking it
              well, making sense of it, and using it to improve is&nbsp;essential.
            </p>
            <p className="quote">
              &ldquo;I was horrified of losing track of feedback. I immediately knew that I needed a
              system to collect and prioritize user&nbsp;feedback.&rdquo;
            </p>
            <p>
              As the amount of feedback grew, managing it became more and more challenging. Stefania
              needed a better&nbsp;solution.
            </p>
            <h2 className="sectionTitle">Solution</h2>
            <p>
              Aside from Canny, Akiflow tried other feedback and product management solutions.
              Nothing worked as well as Canny,&nbsp;though.
            </p>
            <p>
              Akiflow relies on Intercom for customer communication. Automatically tracking feedback
              from Intercom (via our integration) was a major&nbsp;ask.
            </p>
            <p className="quote">
              &ldquo;From the very beginning, we wanted to stop relying on our gut feeling and use
              data. We wanted to collect feedback, prioritize it, and engage with users. We looked
              for a tool that could do all that and found&nbsp;Canny.&rdquo;
            </p>
            <p>
              Now, Akiflow is a feedback powerhouse. Stefania took feedback prioritization to the
              next level with the help of Canny’s roadmap prioritization&nbsp;feature.
            </p>
            <p className="quote">
              &ldquo;One feature I was surprised to find is prioritization based on MRR (monthly
              recurring revenue). This lets me see how many paying customers are requesting a
              certain&nbsp;feature.&rdquo;
            </p>
            <p>Thanks to this feature, Akiflow was even able to bring back lost&nbsp;customers!</p>
            <p className="quote">
              &ldquo;When we first started, we didn’t have a mobile app, and many customers were
              churning because of that. We built the mobile app, notified churned customers through
              Canny, and saw many of them come back. They gave Akiflow another try specifically
              because we notified them about the mobile app they requested&nbsp;earlier.&rdquo;
            </p>
            <p>
              Everybody wants to bring back lost customers. Akiflow achieved that by
              using&nbsp;Canny.
            </p>
            <p>
              Sometimes, you can’t build every feature that customers request. Other times, building
              a feature takes longer than expected. Canny can help in these situations,&nbsp; too.
            </p>
            <p className="quote">
              &ldquo;If there’s a problem, and we’re still working on a fix, we see users talk about
              it inside Canny. They recommend workarounds and help each other. That’s the beauty of
              having a&nbsp;community.&rdquo;
            </p>
            <h2 className="sectionTitle">Results</h2>
            <p>
              Canny doesn’t just help the Akiflow team. It also enhances the user experience for
              Akiflow’s&nbsp;customers.
            </p>
            <p className="quote">
              &ldquo;Our users truly appreciate seeing that we take every piece of feedback into
              consideration. I love seeing how excited users get when I change an idea’s status to
              “in progress”. They also appreciate our transparency. They love knowing what’s coming
              to&nbsp;Akiflow.&rdquo;
            </p>
            <p>
              To quickly keep customers in the loop, Stefania uses Canny’s Slack integration.
              Whenever a user adds a new comment, Slack notifies Stefania. She can then immediately
              engage with them and help resolve any&nbsp;issues.
            </p>
            <p>
              Stefania also enjoys seeing all the changes automatically reflected on the roadmap.
              She doesn’t need to manually go back and forth between tools anymore. This saves her a
              lot of&nbsp;time.
            </p>
            <p>
              Finally, Stefania updates Akiflow’s changelog (in Canny) every time they release a new
              feature (about every month). Seeing thousands of views on each changelog entry makes
              her very excited – she knows that her work doesn’t go unnoticed and that their users
              really&nbsp;care.
            </p>
            <p className="quote">
              &ldquo;Canny really fosters a sense of community for our users. They see that their
              feedback is really shaping our&nbsp;tool.&rdquo;
            </p>
            <p>
              Stefania measures the value that Akiflow gets from Canny in a few different&nbsp;ways:
            </p>
            <ul>
              <li>Number of feature requests</li>
              <li>The implementation rate of suggested features</li>
              <li>Overall customer satisfaction</li>
            </ul>
            <p>
              Stefania recommends that other product management teams fully embrace Canny, just like
              Akiflow&nbsp;does.
            </p>
            <p className="quote">
              &ldquo;Canny isn’t only streamlining feedback management. It also builds a trusting
              relationship with your user&nbsp;base.&rdquo;
            </p>
            <p>
              See how Canny can help your team too –{' '}
              <a target="_blank" href="https://canny.io/request-demo" rel="noopener">
                get a free demo here
              </a>
              !
            </p>
          </div>
          <div className="right">
            <LazyLoadedImage
              className="AkiflowWordmark"
              src={AkiflowWordmark}
              alt="Akiflow Wordmark"
            />
            Akiflow is a YC-backed startup that consolidates various tools into one unified inbox,
            allowing for streamlined task management and calendar&nbsp;scheduling.
            <a href="https://akiflow.com/" rel="noopener" target="_blank">
              akiflow.com
            </a>
            <div className="stat">
              <UppercaseHeader>Employees</UppercaseHeader>
              <p>15</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Founded</UppercaseHeader>
              <p>2020</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Using Canny since</UppercaseHeader>
              <p>2021</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Votes on Canny</UppercaseHeader>
              <p>18,401</p>
            </div>
          </div>
        </div>
        <LandingFooterCTA style="solid" expanded={true} />
      </div>
    </div>
  );
};

export default AkiflowLanding;
